// ── WHICH PRODUCTION GOES ON THE REPORT ──────────────────────────────────────
//
// Kevin, 2026-10-01, on the Claims Manager production review: *"I dont see a button
// to fill production report form, when we get that button for this case, we should
// be able to choose which production to use either Scale or Yield Monitor."*
//
// The review panel already shows every lane of evidence with its RMA production
// record type. What it does NOT do is say which of them you would actually FILE —
// and that is not a presentation gap, it is the one place a production report can
// be wrong in a way that costs a grower a claim.
//
// ⚠⚠ THE SCALE TICKETS AND THE YIELD MONITOR ARE THE SAME BUSHELS, MEASURED TWICE.
// Lawnel Farms, CY2026 WHEAT, Livingston, measured 2026-10-01: scale tickets
// 33,426 bu (RMA basis, 32 tickets re-shrunk) against a John Deere monitor total
// of 32,572 bu over the same 399 harvested acres — 2.6% apart, ONE harvest. Adding
// them reports 65,998 bu, which is roughly double the crop. Nothing on the screen
// stopped anyone doing that, and a production report that overstates production
// reduces or erases an indemnity the grower is owed.
//
// So the two are MUTUALLY EXCLUSIVE and this module refuses to return both.
//
// ⚠ BUT NOT EVERYTHING COMPETES — AND TREATING IT ALL AS A PICK-ONE IS THE
// OPPOSITE ERROR, which UNDERSTATES production and is equally wrong on the form.
// The distinction is what each record MEASURES:
//
//   · A yield monitor weighs the WHOLE harvest as it comes off the field.
//   · The dispositions — sold (A), farm-stored (B/E), fed to livestock (F) — are
//     the SAME harvest after it has been split up, so they SUM to that whole.
//
// Those are two competing totals for one quantity: pick a basis, not a lane.
//   DOCUMENTED = sold + farm-stored + fed      (what the paperwork accounts for)
//   MONITOR    = the yield monitor alone       (what the machine weighed)
//
//   · An APPRAISAL is a DIFFERENT quantity — production on acres that were never
//     harvested — so it ADDS to whichever basis is chosen rather than competing
//     with it. An unharvested appraisal and a scale ticket cannot be the same
//     bushels, because the appraised acres never reached a scale.
//
// ⚠ NOTHING HERE PICKS FOR YOU WITHOUT SAYING SO. The default is the DOCUMENTED
// basis where it exists, because RMA's own vocabulary separates a settlement
// record (A — "Sold/Commercial Storage") from a machine estimate (D — "Automated
// Yield Monitoring System"), and the panel has always labelled the monitor
// "MONITOR ESTIMATE · not claim-of-record". That is a DEFAULT, printed as one, and
// the other basis is always offered with its own figure beside it — never resolved
// silently. The gap between the two is REPORTED, because a 2.6% difference and a
// 40% difference are different conversations and the agent must see which they are in.
//
// ⚠ AND THIS MODULE STAMPS NOTHING — nor can it say whether anything is there to stamp.
// It takes evidence and a basis: no document, no farmer, no query. An earlier version of
// this note read "there is not a single production form in storage to fill", measured
// 2026-10-01 over 304 `farmer_documents` rows. The measurement was true that day and
// asserting it HERE was still wrong, because the staging ladder shipped the next day and
// the sentence kept printing on every farm — a fact that had quietly become a falsehood,
// which is exactly the shape rule zero opens with. The basis choice is this module's whole
// job; whether a form is staged is `api/_production-fill.js`'s lookup, and it says what it
// found. `stampBlocked` now states the SCOPE and makes no claim about any table.

"use strict";

const PR = require("./productionRecord.js");

// The lanes that are DISPOSITIONS of one harvest — they sum, because each names a
// different place the same crop went.
const DISPOSITION_LANES = Object.freeze(["scale_tickets", "bin_measurement", "fed_on_farm"]);
// The lane that WEIGHS the whole harvest in one go, and therefore competes with the
// sum of the dispositions rather than adding to it.
const MONITOR_LANES = Object.freeze(["jd_yield"]);
// Production on acres that were never harvested — a different quantity entirely.
const ADDITIVE_LANES = Object.freeze(["appraisal"]);

const BASIS = Object.freeze({
  documented: {
    key: "documented",
    label: "Scale tickets & farm records",
    short: "Scale",
    lanes: DISPOSITION_LANES,
    why: "What the paperwork accounts for: sold, farm-stored and fed. These are dispositions of one harvest, so they sum.",
  },
  monitor: {
    key: "monitor",
    label: "Yield monitor",
    short: "Yield Monitor",
    lanes: MONITOR_LANES,
    why: "What the machine weighed as the crop came off. It covers the whole harvest on its own, so it is an alternative to the records above — never an addition to them.",
  },
});

const n = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
// Integer hundredths throughout: a production report is arithmetic a grower signs,
// and float addition over a dozen lanes drifts in the last cents.
const cents = (v) => Math.round((n(v) || 0) * 100);
const undo = (c) => c / 100;

const lanesOf = (evidence, keys) =>
  (evidence && Array.isArray(evidence.lanes) ? evidence.lanes : []).filter((l) => l && keys.includes(l.kind));

/**
 * Which bases this evidence can actually support, each with its total and the lanes
 * behind it. A basis with no lanes is NOT offered — an empty option a reader can
 * select is a way to file zero production by accident.
 */
function productionBases(evidence) {
  const out = [];
  for (const b of [BASIS.documented, BASIS.monitor]) {
    const ls = lanesOf(evidence, b.lanes);
    if (!ls.length) continue;
    const quantity = undo(ls.reduce((s, l) => s + cents(l.quantity), 0));
    out.push({
      ...b,
      quantity,
      unit: ls[0].unit || (evidence && evidence.unit) || null,
      lanes: ls.map((l) => ({ kind: l.kind, label: l.label, quantity: n(l.quantity), code: l.code, needsHuman: !!l.needsHuman })),
      // Every record type the chosen basis would put on the form. More than one is
      // normal and correct — a farm that sold some and fed some files both.
      codes: [...new Set(ls.map((l) => l.code).filter(Boolean))],
    });
  }
  return out;
}

/**
 * The line(s) a production report would carry, for ONE chosen basis.
 *
 * `basis` is "documented" | "monitor" | null. Null means "use the default", which is
 * documented-where-it-exists; the result always says which happened and why, so a
 * default is never mistaken for a decision.
 */
function buildProductionReportLines(evidence, basis = null) {
  const bases = productionBases(evidence);
  const refusals = [];
  const unit = (evidence && evidence.unit) || (bases[0] && bases[0].unit) || null;

  // ⚠ APPRAISED PRODUCTION IS COMPUTED BEFORE THE NO-HARVEST RETURN, NOT INSIDE THE HAPPY
  // PATH. Kevin, 2026-10-07, on MULLIGAN FAMILY FARM's card: *"Some of this still is
  // incomplete."* The headline read **Production —** directly above **No claim indicated**,
  // while two sections down the same card listed 224,633 bu appraised in LIVINGSTON and
  // 73,410 in MONROE. `productionBases` reads only the HARVESTED lanes (documented, monitor),
  // so a farm that is entirely appraised and nothing harvested — 65 appraised / 0 harvested
  // here — produced no basis at all, took this early return, and the appraised figure was
  // never even calculated. The verdict beside it WAS computed from those appraisals (+92,042
  // bu over guarantee, which is exactly the two counties' +67,292 and +24,750), so the card
  // stated a conclusion with a dash where its evidence should be.
  //
  // This is the same defect Kevin first reported on the county boxes — *"Production by county
  // says not stated but it's literally in the appraisal"* — one level up, at the farm.
  //
  // ⚠ IT IS STILL NOT SUMMED INTO `quantity`. Appraised acres are unharvested and carry a
  // different RMA record type; folding them into a harvested figure would report standing
  // crop as harvested, which is a different thing in a claim. It rides as its own field and
  // every surface prints it as its own line.
  const appraisalLanes = lanesOf(evidence, ADDITIVE_LANES);
  const appraised = appraisalLanes.length
    ? undo(appraisalLanes.reduce((s, l) => s + cents(l.quantity), 0))
    : null;
  const appraisedLines = appraisalLanes.map((l) => ({
    from: l.kind, label: l.label, quantity: n(l.quantity), unit: l.unit || unit,
    recordType: l.code, recordTypeLabel: l.code ? PR.recordTypeLabel(l.code) : null,
    needsHuman: !!l.needsHuman, harvested: false,
  }));

  if (!bases.length) {
    return {
      ok: false, basis: null, chosen: null, bases: [], lines: appraisedLines, quantity: null, unit,
      appraised,
      defaulted: false, alternative: null, spread: null,
      // ⚠ THE REFUSAL NAMES WHAT IS THERE. "nothing to report" was false on a farm carrying
      // 298,043 appraised bushels — what is absent is the HARVESTED half, and saying so is
      // the difference between a gap and a dead end.
      refusals: [appraised != null
        ? "no harvested production on file — the appraised figure is beside it and is a different record type, so the two are never summed"
        : "no harvested production evidence of any kind — nothing to report"],
      stamps: false, stampBlocked: STAMPING_IS_NOT_THIS_RULE,
    };
  }

  const asked = basis && BASIS[basis] ? basis : null;
  const fallback = bases.find((b) => b.key === "documented") || bases[0];
  const chosen = (asked && bases.find((b) => b.key === asked)) || fallback;
  const defaulted = !asked;
  if (asked && !bases.find((b) => b.key === asked)) {
    // Asked for a basis this evidence cannot support. REFUSE loudly rather than
    // quietly answering with the other one — a silent substitution is how a report
    // comes to carry a figure nobody chose.
    refusals.push(`${BASIS[asked].label} was requested but there is no ${BASIS[asked].label.toLowerCase()} evidence for this crop year — showing ${chosen.label} instead`);
  }

  // The basis NOT chosen, carried so the screen can print it beside the choice. This
  // is the whole reason the control exists: the agent is picking between two
  // measurements of one harvest and must see both numbers.
  const other = bases.find((b) => b.key !== chosen.key) || null;
  let spread = null;
  if (other && chosen.quantity > 0 && other.quantity > 0) {
    const hi = Math.max(chosen.quantity, other.quantity);
    spread = {
      difference: +(chosen.quantity - other.quantity).toFixed(2),
      pct: +(Math.abs(chosen.quantity - other.quantity) / hi * 100).toFixed(1),
      other: other.key, otherLabel: other.label, otherQuantity: other.quantity,
    };
  }

  // The appraised lanes and total were computed above, before the no-harvest return — see
  // the note there for why. They are KEPT as their own lines and never summed into the
  // harvested quantity.
  const lines = [
    ...chosen.lanes.map((l) => ({
      from: l.kind, label: l.label, quantity: l.quantity, unit: chosen.unit,
      recordType: l.code, recordTypeLabel: l.code ? PR.recordTypeLabel(l.code) : null,
      needsHuman: l.needsHuman, harvested: true,
    })),
    ...appraisedLines,
  ];

  for (const l of lines) {
    if (l.needsHuman) refusals.push(`${l.label}: the RMA record type cannot be settled from the data alone — a human must pick it before this line is filed`);
    if (l.quantity == null) refusals.push(`${l.label}: no quantity`);
  }

  return {
    ok: true,
    basis: chosen.key,
    chosen,
    bases,
    defaulted,
    // The default is a claim about our confidence, not about the grower's records, so
    // it carries its reason to the screen rather than being explained in a comment.
    defaultReason: defaulted
      ? (chosen.key === "documented"
          ? "Defaulted to the documented records: RMA's own vocabulary separates a settlement record (A — Sold/Commercial Storage) from a machine estimate (D — Automated Yield Monitoring System)."
          : "Defaulted to the yield monitor because no scale tickets or farm records are on file for this crop year.")
      : null,
    lines,
    // The harvested total for the chosen basis, and the appraised unharvested figure
    // BESIDE it — never summed into one headline, because they are different
    // quantities with different record types and a claim settles them differently.
    quantity: chosen.quantity,
    appraised,
    unit: chosen.unit,
    alternative: other,
    spread,
    refusals,
    // Said out loud rather than implied by a missing button.
    stamps: false,
    stampBlocked: STAMPING_IS_NOT_THIS_RULE,
  };
}

// ⚠ THIS SENTENCE USED TO CLAIM SOMETHING ABOUT A TABLE, AND THE RULE HAS NEVER READ ONE.
// It read "No production form is on file to stamp — farmer_documents holds only acreage
// reports (304 of 304, measured 2026-10-01)". That measurement was true the day it was
// taken and it is not this function's to assert: `buildProductionReportLines` takes evidence
// and a basis, no document, no farmer, no query — so it cannot know whether a form has been
// staged since. Once staging shipped the sentence became a confident falsehood printed on
// every farm, which is rule zero's own failure mode (a plausible story the next reader
// cannot tell from a fact). What the rule CAN say is what it is for.
const STAMPING_IS_NOT_THIS_RULE =
  "This decides WHICH production would be reported. Whether a form is staged to stamp is a separate lookup — the fill panel asks, and says what it found.";

module.exports = {
  BASIS,
  DISPOSITION_LANES,
  MONITOR_LANES,
  ADDITIVE_LANES,
  STAMPING_IS_NOT_THIS_RULE,
  productionBases,
  buildProductionReportLines,
};
